'use strict';

if (!window.customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this.onLoad = this.onLoad.bind(this);
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        threshold: 0,
      });
      this.loaded = false;
    }

    connectedCallback() {
      this.customSlider = this.querySelector('custom-slider');
      this.wrapper = this.querySelector('.recently-viewed-products__grid');
      this.arrows = this.querySelector('.recently-viewed-products__arrows');
      this.observer.observe(this);

      document.addEventListener('DOMContentLoaded', this.onLoad);

      if (!this.loaded) {
        this.loadProductsIfNotVisible();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onLoad);
    }

    onLoad() {
      if (!this.loaded) {
        this.loadProductsIfNotVisible();
      }
    }

    async onIntersect(entries) {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.loaded) {
          this.observer.unobserve(entry.target);
          this.loaded = true;

          const query = this.buildQuery();
          if (query) {
            this.loadProducts(query);
          }
        }
      }
    }

    loadProductsIfNotVisible() {
      if (this.loaded) return;

      this.loaded = true;
      const query = this.buildQuery();

      if (query) {
        this.loadProducts(query);
      }
    }

    buildQuery() {
      const items = JSON.parse(localStorage.getItem('recently-viewed') || '[]');
      const excludeId = this.getAttribute('data-product-id');
      let limit = this.productLimit;

      if (excludeId) {
        const index = items.indexOf(parseInt(excludeId, 10));
        if (index !== -1) {
          items.splice(index, 1);
        }
      } else {
        limit = Math.max(1, limit - 1);
      }

      const limitedItems = items.slice(-limit).reverse();

      return limitedItems.map((id) => `id:${id}`).join(' OR ');
    }

    async loadProducts(query) {
      try {
        const showSwatch = this.dataset.showSwatch === 'true';
        const swatchesParam = showSwatch ? '1' : '0';
        const encodedQuery = encodeURIComponent(query);
        const productDescription = (this.dataset.productDescription || '').trim();
        const productInfo = (this.dataset.productInfo || '').trim();
        let url = `${window.routes.root_url}search?section_id=recently-viewed-list&q=${encodedQuery}&resources[type]=product&recently_viewed_swatches=${swatchesParam}`;

        if (productDescription) {
          url += `&rv_pd=${encodeURIComponent(productDescription)}`;
        }
        if (productInfo) {
          url += `&rv_pi=${encodeURIComponent(productInfo)}`;
        }

        const response = await fetch(url);

        const html = await response.text();
        const products = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#product-grid ul.product-grid');

        if (!products?.hasChildNodes() || !this.wrapper) {
          console.warn('No products found in the response.');
          return;
        }

        const productElements = Array.from(products.children);
        const items = JSON.parse(localStorage.getItem('recently-viewed') || '[]')
          .slice(-this.productLimit)
          .reverse();

        const sortedProducts = items
          .map((id) =>
            productElements.find((el) => {
              let productId = el.querySelector('[data-product-id]')?.getAttribute('data-product-id');
              if (!productId) {
                const cardLink = el.querySelector('a[id^="CardLink-"]');
                productId = cardLink ? cardLink.id.split('-').pop() : null;
              }
              return productId && productId === id.toString();
            })
          )
          .filter(Boolean);

        if (!sortedProducts.length) return;

        sortedProducts.forEach((el) => this.applyCardSettings(el));

        const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
        const enableSliderSetting = this.dataset.enableDesktopSlider === 'true';
        const showSlider = enableSliderSetting && sortedProducts.length > columnsDesktop;

        this.wrapper.innerHTML = '';
        sortedProducts.forEach((el) => {
          if (showSlider) {
            el.classList.add('swiper-slide');
          } else {
            el.classList.remove('swiper-slide');
          }
          this.wrapper.appendChild(el);
        });

        this.wrapper.classList.toggle('swiper-wrapper', showSlider);

        if (this.arrows) {
          this.arrows.hidden = !showSlider;
        }

        this.initOrUpdateSlider(showSlider);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }

    applyCardSettings(el) {
      if (this.dataset.showSwatch !== 'true') {
        el.querySelectorAll('.card-variants-wrapp').forEach((node) => node.remove());
      } else {
        const allowedNames = (this.dataset.productVariantsName || 'Color')
          .split(',')
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);

        el.querySelectorAll('.card-variants-wrapp').forEach((wrap) => {
          const optionName = (wrap.dataset.optionName || '').trim().toLowerCase();
          if (optionName && !allowedNames.includes(optionName)) {
            wrap.remove();
          }
        });
      }

      if (this.dataset.showVendor !== 'true') {
        el.querySelectorAll('.card-vendor').forEach((node) => node.remove());
      }

      if (this.dataset.showType !== 'true') {
        el.querySelectorAll('.card-information > .caption-with-letter-spacing.light:not(.card-vendor)').forEach((node) =>
          node.remove()
        );
      }

      if (this.dataset.showRating !== 'true') {
        el.querySelectorAll('.rating, .rating-text, .rating-count').forEach((node) => node.remove());
      }

      if (this.dataset.quickAdd === 'none') {
        el.querySelectorAll('.quick-add, quick-add-modal').forEach((node) => node.remove());
      }

      if (this.dataset.showSecondaryImage !== 'true') {
        el.querySelectorAll('.media img:not(:first-of-type)').forEach((node) => node.remove());
        el.querySelectorAll('.media').forEach((media) => {
          media.className = media.className
            .split(/\s+/)
            .filter((cls) => !/^media-\d+$/.test(cls))
            .join(' ');
        });
      }

      if (!(this.dataset.productDescription || '').trim()) {
        el.querySelectorAll('.card-product-description').forEach((node) => node.remove());
      }

      if (!(this.dataset.productInfo || '').trim()) {
        el.querySelectorAll('.card-product-info').forEach((node) => node.remove());
      }

      this.applyImageRatio(el);
    }

    applyImageRatio(el) {
      const ratioSetting = this.dataset.imageRatio || 'adapt';
      if (ratioSetting === 'adapt') return;

      const ratioMap = {
        portrait: 0.8,
        landscape: 1.2,
        square: 1,
      };
      const ratio = ratioMap[ratioSetting];
      if (!ratio) return;

      const percent = `${(1 / ratio) * 100}%`;
      el.querySelectorAll('.card, .card__inner').forEach((node) => {
        node.style.setProperty('--ratio-percent', percent);
      });
    }

    initOrUpdateSlider(showSlider) {
      if (!this.customSlider) return;

      if (typeof this.customSlider.destroySwiper === 'function') {
        this.customSlider.destroySwiper();
      }

      this.customSlider.slider = this.customSlider.querySelector('.swiper');

      if (!showSlider || !this.customSlider.slider) return;

      if (typeof this.customSlider.initSwiper === 'function') {
        this.customSlider.initSwiper();
      }
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    get productLimit() {
      return parseInt(this.getAttribute('data-products-limit'), 10) || 4;
    }
  }

  window.customElements.define('recently-viewed-products', RecentlyViewedProducts);
}